import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFeedback } from '@/components/feedback/feedback-context'
import { resetPortalPassword } from '@/features/auth/password-reset/api'
import { normalizeLanguage } from '@/i18n/i18n'
import { clearPortalSession } from '@/lib/auth/portal-session'
import { portalLoginRoutePath } from '@/lib/auth/portal-routes'
import {
  appendUserLoginResumeMarker,
  persistPortalLoginPrefillEmail,
  readStoredPortalAccount,
} from '@/lib/auth/session-storage'
import type { PortalKind } from '@/lib/auth/token-storage'

type PasswordResetErrors = Partial<
  Record<'currentPassword' | 'newPassword' | 'confirmPassword' | 'root', string>
>

export function usePasswordResetPage(portal: PortalKind) {
  const { i18n, t } = useTranslation('auth')
  const { showFeedback } = useFeedback()
  const language = normalizeLanguage(i18n.language)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errors, setErrors] = useState<PasswordResetErrors>({})
  const recoveryPasswordEntry =
    portal === 'user' &&
    new URLSearchParams(window.location.search).get('login_entry') === 'password'

  const loginPathForEntry = () =>
    recoveryPasswordEntry ? '/login/password' : portalLoginRoutePath[portal]

  const finishWithLoginRedirect = () => {
    persistPortalLoginPrefillEmail(portal, readStoredPortalAccount(portal).email)
    clearPortalSession(portal)
    const loginPath = `${loginPathForEntry()}?reset=success`
    window.location.assign(portal === 'user' ? appendUserLoginResumeMarker(loginPath) : loginPath)
  }

  const resetMutation = useMutation({
    mutationFn: () => resetPortalPassword(portal, { currentPassword, newPassword }),
    onError: () => setErrors({ root: t('passwordReset.errors.submitFailed') }),
    onSuccess: finishWithLoginRedirect,
  })

  const validate = () => {
    const nextErrors: PasswordResetErrors = {}
    if (!currentPassword.trim()) {
      nextErrors.currentPassword = t('passwordReset.validation.currentPasswordRequired')
    }
    if (!newPassword.trim()) {
      nextErrors.newPassword = t('passwordReset.validation.newPasswordRequired')
    }
    if (!confirmPassword.trim()) {
      nextErrors.confirmPassword = t('passwordReset.validation.confirmPasswordRequired')
    } else if (newPassword !== confirmPassword) {
      nextErrors.confirmPassword = t('passwordReset.validation.passwordMismatch')
    }
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const cancel = () => {
    clearPortalSession(portal)
    showFeedback({ message: t('passwordReset.cancelled'), tone: 'info' })
    window.location.assign(loginPathForEntry())
  }

  return {
    cancel,
    confirmPassword,
    currentPassword,
    errors,
    language,
    newPassword,
    portal,
    resetMutation,
    showFeedback,
    setConfirmPassword,
    setCurrentPassword,
    setErrors,
    setNewPassword,
    t,
    validate,
  }
}
